import { computed, signal } from '@angular/core'

import {
  IColumn,
  IRequestLine,
  IRequestLineBase,
  IRequestListState,
  IRequiredFiles,
  ISearchParams,
} from '../../models/portability-request.model'
import { HttpErrorResponse } from '@angular/common/http'
import { ApiService } from '../api/api.service'
import { NotificationService } from '../notification.service'

export const DEFAULT_SIZE_PAGE = 100
export const DEFAULT_PAGE = {
  page: 0,
  size: DEFAULT_SIZE_PAGE,
}
export class PortabilityRequestsBaseService<T> {
  protected expectedFilesMap: { [key: string]: IRequiredFiles[] } = {}
  protected readonly DOWNLOAD_PORTABILITY_REQUEST_DATA_API_ENDPOINT =
    '/portability-request/'

  constructor(
    protected apiService: ApiService,
    protected notificationService: NotificationService,
  ) {}

  expectedFiles = signal<{ [key: string]: IRequiredFiles[] }>({})

  readonly tableColumns: IColumn[] = [
    {
      key: 'fromApi',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.ORIGINE',
    },
    {
      key: 'requestId',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.ID',
    },
    {
      key: 'submittedAt',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.SUBMITTED_AT',
    },
    {
      key: 'lastName',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.LASTNAME',
    },
    {
      key: 'firstName',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.FIRSTNAME',
    },
    {
      key: 'birthDate',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.BIRTHDATE',
    },
    {
      key: 'email',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.EMAIL',
    },
    {
      key: 'address',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.ADDRESS',
    },
    {
      key: 'phoneNumber',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.PHONE',
    },
    {
      key: 'loyaltyCardId',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.LOYALTY_CARD_ID',
    },
    {
      key: 'files',
      label: 'MANDATARY.REQUESTS_LIST.TABLE.COLUMNS.DOCUMENTS',
    },
    {
      key: 'status',
      label:
        'ADMIN.PORTABILITY_REQUESTS_AND_QUALIFICATION_LIST.TABLE.COLUMNS.STATUS',
    },
  ]

  protected state = signal<IRequestListState<T>>({
    requestList: [],
    loading: false,
    error: null,
    size: DEFAULT_SIZE_PAGE,
    first: 0,
    totalElements: 0,
  })

  filterRequest = signal<ISearchParams>({
    page: 0,
    size: DEFAULT_SIZE_PAGE,
  })

  public requestList = computed(() => this.state().requestList)
  public size = computed(() => this.state().size)
  public first = computed(() => this.state().first)
  public totalElements = computed(() => this.state().totalElements)
  public loading = computed(() => this.state().loading)
  public error = computed(() => this.state().error)

  public requestListCount = computed(() => this.requestList().length)

  protected setLoading(loading: boolean): void {
    this.state.update((state) => ({ ...state, loading }))
  }

  protected clearError(): void {
    this.state.update((state) => ({ ...state, error: null }))
  }

  protected handleError(error: HttpErrorResponse): void {
    this.state.update((state) => ({
      ...state,
      error: error.message,
      loading: false,
    }))
  }

  protected updateState(partialState: Partial<IRequestListState<T>>): void {
    this.state.update((state) => ({
      ...state,
      ...partialState,
    }))
  }

  initializeExpectedFiles(
    rowId: string,
    requiredFileTemplates: IRequiredFiles[],
    portabilityRequestFiles?: IRequestLine['files'],
  ) {
    if (!this.expectedFilesMap[rowId]) {
      const initializedFiles = requiredFileTemplates.map((template, index) => {
        const fileKey = this.getFileKeyForIndex(index)

        if (portabilityRequestFiles?.[fileKey]) {
          return {
            ...template,
            file: new File(
              [new Blob()],
              portabilityRequestFiles[fileKey]!.fileName,
              { type: 'application/pdf' },
            ),
          }
        }

        return { ...template }
      })

      this.expectedFilesMap[rowId] = initializedFiles
      this.expectedFiles.set({ ...this.expectedFilesMap })
    }
  }

  protected getFileKeyForIndex(index: number): keyof IRequestLine['files'] {
    const fileKeys: Array<keyof IRequestLine['files']> = [
      'MANDATE',
      'DATA_PROCESSING_MANAGER',
      'SIGNATURE_CERTIFICATE',
      'STORE_APPENDIX',
    ]
    return fileKeys[index]
  }

  getRowFilenames(rowId: string): string[] {
    if (!this.expectedFilesMap[rowId]) return []
    return this.expectedFilesMap[rowId]
      .filter((item) => item.file !== null)
      .map((item) => item.filename)
  }

  getExpectedFilesWithDesc(rowId: string): IRequiredFiles[] {
    return this.expectedFilesMap[rowId] || []
  }

  getExpectedFiles(rowId: string): File[] {
    return this.getExpectedFilesWithDesc(rowId)
      .filter((expectedFile) => expectedFile.file !== null)
      .map((expectedFile) => expectedFile.file!)
  }

  updatePortabilityRequestFromState(id: string, updatedRequest: T) {
    this.state.update((state) => ({
      ...state,
      requestList: state.requestList.map((request) =>
        (request as IRequestLineBase).id === id ? updatedRequest : request,
      ),
    }))
  }

  removePortabilityRequestFromState(id: string) {
    this.state.update((state) => ({
      ...state,
      requestList: state.requestList.filter(
        (request) => (request as IRequestLineBase).id !== id,
      ),
    }))
  }

  formatDate(date: string) {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  getTableColumns(neededColumns?: IColumn['key'][]) {
    if (!neededColumns) {
      return this.tableColumns
    }

    return this.tableColumns.filter((col) => neededColumns.includes(col.key))
  }

  onDownloadPortabilityData(id: string, requestId: string) {
    if (!this.apiService || !this.notificationService) {
      throw new Error('ApiService and NotificationService must be injected')
    }

    this.apiService
      .get<Blob>(
        `${this.DOWNLOAD_PORTABILITY_REQUEST_DATA_API_ENDPOINT + id}/files-download`,
        {},
        {},
        { responseType: 'blob' },
      )
      .subscribe({
        next: (zipBlob?: Blob) => {
          if (!zipBlob) {
            this.notificationService!.showToast(
              'error',
              'COMMON.ERROR',
              'COMMON.DOWNLOAD_FILE.NO_FILES_AVAILABLE',
            )
          } else {
            const url = window.URL.createObjectURL(zipBlob)
            const a = document.createElement('a')
            a.href = url
            a.download = requestId
            document.body.appendChild(a)
            a.click()

            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)

            this.notificationService!.showToast(
              'success',
              'COMMON.SUCCESS',
              'COMMON.DOWNLOAD_FILE.FILE_DOWNLOADED_SUCCESSFULLY',
            )
          }
        },
        error: (error) => {
          console.error('Erreur lors du téléchargement du ZIP:', error)
          this.notificationService!.showToast(
            'error',
            'COMMON.ERROR',
            'COMMON.DOWNLOAD_FILE.FILE_DOWNLOAD_ERROR',
          )
        },
      })
  }

  resetPagination() {
    this.state.update((state) => ({
      ...state,
      first: 0,
    }))
  }
}
